/**
 * The back office's furniture (ADD-ON 25) — a flat, high-contrast admin kit.
 * Three surfaces: the floor is #f5f5f5, every panel is white, chrome is #fafafa,
 * and they are told apart by one hard 1px #d0d0d0 line — never a shadow. Corners
 * are 2px. Type is Inter, near-black; the lightest grey allowed is #4a4a4a.
 * Color only carries meaning: blue for the primary action, green / amber / red
 * for status. Everything else is black, white or grey.
 */
export const BO = {
    ink: "#1a1a1a", sub: "#4a4a4a", line: "#d0d0d0", page: "#f5f5f5", chrome: "#fafafa",
    blue: "#1a56db", green: "#067647", amber: "#b54708", red: "#b42318",
};

export const inputCls =
    "w-full border border-[#d0d0d0] bg-white px-2 py-1.5 text-sm font-medium text-[#1a1a1a] outline-none placeholder:font-normal placeholder:text-[#767676] focus:border-[#1a1a1a]";

/** Same input, sized by the caller — for spreadsheet cells that must not stretch. */
export const cellCls =
    "border border-[#d0d0d0] bg-white px-1 py-0.5 text-center text-sm font-medium text-[#1a1a1a] outline-none placeholder:font-normal placeholder:text-[#767676] focus:border-[#1a1a1a]";

/** Kept so every screen's `tone={TONES.x}` keeps working — the admin runs on ink now. */
export const TONES = {
    home: BO.ink, orders: BO.ink, listings: BO.ink, marketing: BO.ink,
    messages: BO.ink, insights: BO.ink, settings: BO.ink,
};

/** Status colors. `ink` and `blue` are solid objects, so their type is inverted. */
export const CHIP = {
    neutral: "border-[#d0d0d0] bg-[#f5f5f5] text-[#1a1a1a]",
    ink: "border-[#1a1a1a] bg-[#1a1a1a] text-white bo-inv",
    blue: "border-[#1a56db] bg-[#eff4ff] text-[#1a56db]",
    green: "border-[#067647] bg-[#ecfdf3] text-[#067647]",
    amber: "border-[#b54708] bg-[#fffaeb] text-[#b54708]",
    red: "border-[#b42318] bg-[#fef3f2] text-[#b42318]",
};

export function Chip({ tone = "neutral", children, testid, cls }) {
    return (
        <span data-testid={testid}
            className={`inline-flex items-center gap-1 border px-1.5 py-px text-[11px] font-semibold uppercase ${cls || CHIP[tone] || CHIP.neutral}`}>
            {children}
        </span>
    );
}

export function PageHead({ kicker, title, sub, right, testid }) {
    return (
        <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1.5 border-b border-[#d0d0d0] pb-2"
            data-testid={testid}>
            <div className="min-w-0">
                {kicker && (
                    <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4a4a4a]">{kicker}</p>
                )}
                <h1 className="text-[19px] font-bold leading-tight tracking-tight text-[#1a1a1a]">{title}</h1>
                {sub && <p className="mt-0.5 max-w-4xl text-sm leading-snug text-[#4a4a4a]">{sub}</p>}
            </div>
            {right && <div className="shrink-0">{right}</div>}
        </div>
    );
}

export function Section({ id, title, note, right, children, testid, pad = true }) {
    return (
        <section id={id} data-testid={testid} className="scroll-mt-28 border border-[#d0d0d0] bg-white">
            {(title || note || right) && (
                <header className="flex flex-wrap items-center gap-x-2 gap-y-0.5 border-b border-[#d0d0d0] bg-[#fafafa] px-2.5 py-1.5">
                    {title && <h2 className="text-sm font-semibold text-[#1a1a1a]">{title}</h2>}
                    {note && <p className="text-[13px] text-[#4a4a4a]">{note}</p>}
                    {right && <div className="ml-auto">{right}</div>}
                </header>
            )}
            <div className={pad ? "p-2.5" : ""}>{children}</div>
        </section>
    );
}

export function Toggle({ on, onChange, disabled, testid, labelOn = "On", labelOff = "Off" }) {
    return (
        <button type="button" role="switch" aria-checked={!!on} disabled={disabled} data-testid={testid}
            onClick={() => onChange(!on)}
            className="inline-flex items-center gap-1.5 disabled:opacity-50">
            <span className={`relative block h-[18px] w-8 border ${on
                ? "border-[#1a1a1a] bg-[#1a1a1a]" : "border-[#d0d0d0] bg-white"}`}>
                <span className={`absolute top-[2px] block h-[12px] w-[12px] ${on
                    ? "left-[16px] bg-white" : "left-[2px] bg-[#4a4a4a]"}`} />
            </span>
            <span className="text-[11px] font-semibold uppercase text-[#1a1a1a]">{on ? labelOn : labelOff}</span>
        </button>
    );
}

export function Pill({ active, children, onClick, testid, count }) {
    return (
        <button onClick={onClick} data-testid={testid}
            className={`inline-flex items-center gap-1.5 border px-2 py-1 text-[13px] font-semibold ${active
                ? "border-[#1a1a1a] bg-[#1a1a1a] text-white bo-inv"
                : "border-[#d0d0d0] bg-white text-[#1a1a1a] hover:border-[#1a1a1a]"}`}>
            {children}
            {count != null && (
                <span className={`tabular-nums ${active ? "opacity-80" : "text-[#4a4a4a]"}`}>{count}</span>
            )}
        </button>
    );
}

export function Btn({ children, onClick, disabled, testid, type = "button", tone = "line" }) {
    const cls = tone === "solid"
        ? "border-[#1a56db] bg-[#1a56db] text-white bo-inv hover:bg-[#1544b0]"
        : tone === "danger"
            ? "border-[#d0d0d0] bg-white text-[#b42318] hover:border-[#b42318]"
            : "border-[#d0d0d0] bg-white text-[#1a1a1a] hover:border-[#1a1a1a]";
    return (
        <button type={type} onClick={onClick} disabled={disabled} data-testid={testid}
            className={`inline-flex items-center gap-1.5 border px-2 py-1 text-[13px] font-semibold disabled:opacity-40 ${cls}`}>
            {children}
        </button>
    );
}


/**
 * One setting per line, like a ledger: what it is on the left (with one plain sentence under it),
 * the control on the right. Stack these inside a Section and the eye reads straight down.
 */
export function FieldRow({ label, hint, children, testid, top = false }) {
    return (
        <div className={`grid gap-1 border-t border-[#e5e5e5] px-2.5 py-2 first:border-t-0 sm:grid-cols-[minmax(160px,220px)_minmax(0,1fr)] sm:gap-4 ${top ? "" : "sm:items-center"}`}
            data-testid={testid}>
            <div className="min-w-0">
                <p className="text-[13px] font-semibold leading-tight text-[#1a1a1a]">{label}</p>
                {hint && <p className="mt-0.5 text-[12px] leading-snug text-[#4a4a4a]">{hint}</p>}
            </div>
            <div className="min-w-0">{children}</div>
        </div>
    );
}
